import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TooltipLabel } from '../ui/TooltipLabel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { financialManagementTooltips } from '../../utils/financialTooltips';

/**
 * Dynamic interest rate fields based on interest type
 * - Fixed: Single rate field
 * - Variable: Base rate + Margin
 * - Floating: Reference rate + Spread with cap/floor
 */
const DynamicInterestFields = ({ form, setForm }) => {
  const interestType = form.interest_type || 'fixed';
  
  // Calculate effective rate for variable/floating
  const calculateEffectiveRate = () => {
    if (interestType === 'variable' && form.base_rate && form.margin) {
      return parseFloat(form.base_rate) + parseFloat(form.margin);
    }
    return parseFloat(form.interest_rate) || 0;
  };

  return (
    <div className="space-y-4">
      {/* Interest Type Selection */}
      <div>
        <TooltipLabel htmlFor="interest-type" tooltip={financialManagementTooltips.interestType}>
          Interest Type *
        </TooltipLabel>
        <Select 
          value={interestType} 
          onValueChange={(val) => {
            // Reset rate fields when type changes
            setForm({
              ...form, 
              interest_type: val,
              interest_rate: '',
              base_rate: '',
              margin: '',
              reference_rate_name: '',
              rate_cap: '',
              rate_floor: ''
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed Rate - Interest stays constant</SelectItem>
            <SelectItem value="variable">Variable Rate - Changes with market (Base + Margin)</SelectItem>
            <SelectItem value="floating">Floating Rate - Tied to reference rate with adjustments</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* FIXED RATE FIELDS */}
      {interestType === 'fixed' && (
        <div>
          <TooltipLabel htmlFor="interest-rate" tooltip={financialManagementTooltips.fixedRate}>
            Interest Rate (%) *
          </TooltipLabel>
          <Input
            id="interest-rate"
            type="number"
            step="0.1"
            value={form.interest_rate}
            onChange={(e) => setForm({...form, interest_rate: e.target.value})}
            placeholder="e.g., 8.5"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Fixed annual interest rate that remains constant throughout loan term
          </p>
        </div>
      )}

      {/* VARIABLE RATE FIELDS */}
      {interestType === 'variable' && (
        <div className="space-y-4 border rounded-lg p-4 bg-blue-50">
          <h4 className="font-medium text-blue-800">Variable Rate Configuration</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <TooltipLabel htmlFor="base-rate" tooltip={financialManagementTooltips.baseRate}>
                Base Rate (%) *
              </TooltipLabel>
              <Input
                id="base-rate"
                type="number"
                step="0.1"
                value={form.base_rate}
                onChange={(e) => {
                  setForm({...form, base_rate: e.target.value});
                  // Auto-calculate total rate
                  if (form.margin) {
                    const total = parseFloat(e.target.value) + parseFloat(form.margin);
                    setForm(prev => ({...prev, base_rate: e.target.value, interest_rate: total.toString()}));
                  }
                }}
                placeholder="e.g., 7.0"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Market reference rate (e.g., Prime, LIBOR)</p>
            </div>

            <div>
              <TooltipLabel htmlFor="margin" tooltip={financialManagementTooltips.margin}>
                Margin (%) *
              </TooltipLabel>
              <Input
                id="margin"
                type="number"
                step="0.1"
                value={form.margin}
                onChange={(e) => {
                  setForm({...form, margin: e.target.value});
                  // Auto-calculate total rate
                  if (form.base_rate) {
                    const total = parseFloat(form.base_rate) + parseFloat(e.target.value);
                    setForm(prev => ({...prev, margin: e.target.value, interest_rate: total.toString()}));
                  }
                }}
                placeholder="e.g., 2.0"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Spread added to base rate</p>
            </div>
          </div>

          <div>
            <TooltipLabel htmlFor="adjustment-frequency" tooltip={financialManagementTooltips.adjustmentFrequency}>
              Adjustment Frequency
            </TooltipLabel>
            <Select value={form.adjustment_frequency || ''} onValueChange={(val) => setForm({...form, adjustment_frequency: val})}>
              <SelectTrigger>
                <SelectValue placeholder="How often rate adjusts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly - Rate adjusts every month</SelectItem>
                <SelectItem value="quarterly">Quarterly - Rate adjusts every 3 months</SelectItem>
                <SelectItem value="semi_annual">Semi-Annual - Rate adjusts every 6 months</SelectItem>
                <SelectItem value="annual">Annual - Rate adjusts yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Calculated Total Rate */}
          <div className="bg-white border border-blue-200 rounded p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Effective Interest Rate:</span>
              <span className="text-lg font-bold text-blue-900">
                {calculateEffectiveRate().toFixed(2)}%
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {form.base_rate && form.margin ? 
                `${form.base_rate}% (base) + ${form.margin}% (margin) = ${calculateEffectiveRate().toFixed(2)}%` :
                'Enter base rate and margin to calculate'
              }
            </p>
          </div>
        </div>
      )}

      {/* FLOATING RATE FIELDS */}
      {interestType === 'floating' && (
        <div className="space-y-4 border rounded-lg p-4 bg-green-50">
          <h4 className="font-medium text-green-800">Floating Rate Configuration</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <TooltipLabel htmlFor="reference-rate" tooltip={financialManagementTooltips.referenceRate}>
                Reference Rate Name *
              </TooltipLabel>
              <Select value={form.reference_rate_name || ''} onValueChange={(val) => setForm({...form, reference_rate_name: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reference" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LIBOR">LIBOR (London Interbank Offered Rate)</SelectItem>
                  <SelectItem value="PRIME">Prime Rate</SelectItem>
                  <SelectItem value="TREASURY">Treasury Rate</SelectItem>
                  <SelectItem value="SOFR">SOFR (Secured Overnight Financing Rate)</SelectItem>
                  <SelectItem value="EURIBOR">EURIBOR (Euro Interbank Offered Rate)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <TooltipLabel htmlFor="base-rate-float" tooltip="Current value of reference rate">
                Current Rate (%) *
              </TooltipLabel>
              <Input
                id="base-rate-float"
                type="number"
                step="0.1"
                value={form.base_rate}
                onChange={(e) => setForm({...form, base_rate: e.target.value})}
                placeholder="e.g., 5.5"
                required
              />
            </div>

            <div>
              <TooltipLabel htmlFor="margin-float" tooltip="Spread over reference rate">
                Spread (%) *
              </TooltipLabel>
              <Input
                id="margin-float"
                type="number"
                step="0.1"
                value={form.margin}
                onChange={(e) => setForm({...form, margin: e.target.value})}
                placeholder="e.g., 2.5"
                required
              />
            </div>

            <div>
              <TooltipLabel htmlFor="rate-cap" tooltip={financialManagementTooltips.rateCap}>
                Rate Cap (%) - Optional
              </TooltipLabel>
              <Input
                id="rate-cap"
                type="number"
                step="0.1"
                value={form.rate_cap}
                onChange={(e) => setForm({...form, rate_cap: e.target.value})}
                placeholder="e.g., 12"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum rate protection</p>
            </div>

            <div>
              <TooltipLabel htmlFor="rate-floor" tooltip={financialManagementTooltips.rateFloor}>
                Rate Floor (%) - Optional
              </TooltipLabel>
              <Input
                id="rate-floor"
                type="number"
                step="0.1"
                value={form.rate_floor}
                onChange={(e) => setForm({...form, rate_floor: e.target.value})}
                placeholder="e.g., 5"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum rate guarantee</p>
            </div>

            <div>
              <TooltipLabel htmlFor="adjustment-freq-float" tooltip="How often rate resets">
                Adjustment Frequency
              </TooltipLabel>
              <Select value={form.adjustment_frequency || ''} onValueChange={(val) => setForm({...form, adjustment_frequency: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Rate reset period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicInterestFields;
